import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Tooltip,
  Popover,
  Button
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import useApi from 'hooks/useApi';
import { useParams } from 'react-router';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { useTheme } from '@mui/material/styles';

type ReportItem = {
  _id: string;
  name: string;
  uid: string;
};

const ReportTableManager = ({ onClose }: { onClose: () => void }) => {
  const theme = useTheme();
  const { teamUid } = useParams();
  const { getNodeReports, createNodeReport, updateNodeReport, deleteNodeReport } = useApi();

  const [reports, setReports] = useState<ReportItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [initialValues, setInitialValues] = useState({ uid: '', name: '' });
  const [addAnchorEl, setAddAnchorEl] = useState<null | HTMLElement>(null);
  const [deleteAnchorEl, setDeleteAnchorEl] = useState<null | HTMLElement>(null);
  const [reportToDelete, setReportToDelete] = useState<ReportItem | null>(null);

  const validationSchema = Yup.object({
    name: Yup.string().required('Report Name is required'),
    uid: Yup.string().optional()
  });

  const fetchReports = async () => {
    if (!teamUid) return;
    setLoading(true);
    try {
      const res = await getNodeReports(teamUid);
      const sorted = Array.isArray(res?.data)
        ? [...res.data].sort((a: any, b: any) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }))
        : [];
      setReports(sorted);
    } catch {
      setReports([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReports();
  }, [teamUid]);

  const handleDeleteReport = async () => {
    if (!reportToDelete || !teamUid) return;

    setLoading(true);
    try {
      await deleteNodeReport(reportToDelete.uid);
      await fetchReports();
      setDeleteAnchorEl(null);
      setReportToDelete(null);
    } catch (error: any) {
      console.error('Delete failed', error);
      alert(error?.response?.data?.message || 'Failed to delete report');
    } finally {
      setLoading(false);
    }
  };

  const submitFormData = async (data: { uid: string; name: string }, setSubmitting: (isSub: boolean) => void) => {
    if (!teamUid) return;
    setSubmitting(true);
    try {
      if (data.uid) {
        await updateNodeReport({ name: data.name }, data.uid);
      } else {
        await createNodeReport({ name: data.name }, teamUid);
      }
      // Stay in the panel and show the new list rather than closing the drawer.
      await fetchReports();
    } catch (error) {
      console.error('Submission failed', error);
    } finally {
      setSubmitting(false);
      setAddAnchorEl(null);
    }
  };

  return (
    <>
      <Box p={2}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          mb={2}
          sx={{ backgroundColor: theme.palette.background.paper }}
        >
          <Typography variant="h6" fontWeight={600}>
            Manage Reports
          </Typography>
          <Tooltip title="Add new report">
            <IconButton
              color="primary"
              onClick={(e) => {
                setAddAnchorEl(e.currentTarget);
                setInitialValues({ uid: '', name: '' });
              }}
              sx={{ color: theme.palette.primary.main }}
            >
              <AddCircleOutlineIcon fontSize="large" />
            </IconButton>
          </Tooltip>
        </Box>

        <TableContainer
          component={Paper}
          sx={{
            '& .MuiTableCell-head': {
              fontWeight: 'bold',
              backgroundColor: theme.palette.background.default
            }
          }}
        >
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell>Report Names</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {reports.map((report) => (
                <TableRow
                  key={report.uid}
                  hover
                  sx={{
                    '&:hover': {
                      backgroundColor: theme.palette.action.hover
                    }
                  }}
                >
                  <TableCell>{report.name}</TableCell>
                  <TableCell align="right">
                    <Box display="flex" justifyContent="flex-end" gap={1}>
                      <Tooltip title="Edit report">
                        <IconButton
                          size="medium"
                          color="primary"
                          onClick={(e) => {
                            setAddAnchorEl(e.currentTarget);
                            setInitialValues({ name: report.name, uid: report.uid });
                          }}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete report">
                        <IconButton
                          size="medium"
                          color="error"
                          onClick={(e) => {
                            setDeleteAnchorEl(e.currentTarget);
                            setReportToDelete(report);
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
              {reports.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={2} align="center">
                    No reports found.
                  </TableCell>
                </TableRow>
              )}
              {loading && (
                <TableRow>
                  <TableCell colSpan={2} align="center">
                    Loading...
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Add / Update Report Popover */}
        <Popover
          open={Boolean(addAnchorEl)}
          anchorEl={addAnchorEl}
          onClose={() => setAddAnchorEl(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            enableReinitialize
            onSubmit={(values, { setSubmitting }) => {
              submitFormData(values, setSubmitting);
            }}
          >
            {({ values, errors, touched, handleChange, handleBlur, handleSubmit, isValid, isSubmitting }) => (
              <Box component={'form'} onSubmit={handleSubmit} p={2} sx={{ width: 400 }}>
                <Typography variant="subtitle1" mb={2}>
                  {values.uid ? 'Update' : 'Add'} Report
                </Typography>
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography sx={{ minWidth: 100 }}>Report Name</Typography>
                  <TextField
                    name="name"
                    fullWidth
                    defaultValue={values.name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={Boolean(errors.name && touched.name)}
                  />
                </Box>
                <Box mt={2} display="flex" justifyContent="flex-end" gap={1}>
                  <Button type="button" onClick={() => setAddAnchorEl(null)}>
                    Cancel
                  </Button>
                  <Button variant="contained" type="submit" disabled={!isValid || isSubmitting}>
                    {values.uid ? 'Update' : 'Add'}
                  </Button>
                </Box>
              </Box>
            )}
          </Formik>
        </Popover>
      </Box>

      {/* Delete Confirmation Popover */}
      <Popover
        open={Boolean(deleteAnchorEl)}
        anchorEl={deleteAnchorEl}
        onClose={() => {
          setDeleteAnchorEl(null);
          setReportToDelete(null);
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Box p={2} sx={{ width: 240 }}>
          <Typography variant="body2" mb={2}>
            Delete "{reportToDelete?.name}"?
          </Typography>
          <Box display="flex" justifyContent="flex-end" gap={1}>
            <Button
              size="small"
              onClick={() => {
                setDeleteAnchorEl(null);
                setReportToDelete(null);
              }}
            >
              Cancel
            </Button>
            <Button size="small" variant="contained" color="error" onClick={handleDeleteReport} disabled={loading}>
              {loading ? 'Deleting...' : 'Delete'}
            </Button>
          </Box>
        </Box>
      </Popover>
    </>
  );
};

export default ReportTableManager;
